import React, { useState, useCallback } from "react";
import { ChevronDown, Filter, XCircle, X } from "lucide-react";
import axios from "axios";
import '../styles/GlobalStyles.css';

const FilterSidebar = ({ onResults, initialFilters = {} }) => {
  const filters = [
    {
      label: "Subject",
      key: "skill",
      options: [
        "Mathematics",
        "English",
        "Science",
        "Social Studies",
        "Computer Science",
        "Physical Education",
        "Hindi",
        "Kannada",
        "Physics",
        "Chemistry",
        "Biology"
      ],
    },

    {
      label: "Location",
      key: "distance",
      options: ["5 km", "10 km", "20 km"],
    },
    {
      label: "Education Qualification",
      key: "education",
      options: [
        "NTT (Nursery Teacher Training)",
        "D.El.Ed (Diploma in Elementary Education)",
        "B.Ed (Bachelor of Education)",
        "Integrated Teacher Education Program (ITEP)",
        "Teacher Eligibility Tests (TET/CTET etc.)",
        "Post-Graduation (MA/MSc/MCom)",
        "PhD",
        "NET / SET"
      ],
    },
    {
      label: "Experience",
      key: "experience",
      options: ["0-2 years", "3-5 years", "6-10 years", "10+ years"],
    },
    {
      label: "Gender",
      key: "gender",
      options: ["Any", "Male", "Female", "Other"],
    },
    {
      label: "Salary Range",
      key: "salary",
      options: ["Below 3 LPA", "3-5 LPA", "5-8 LPA", "Above 8 LPA"],
    },
    {
      label: "Employment Type",
      key: "employmentType",
      options: ["Full Time", "Part Time"],
    },
    {
      label: "Availability / Notice Period",
      key: "noticePeriod",
      options: ["Immediate", "15 Days", "30 Days", "45 Days", "60 Days", "75 Days", "90 Days"],
    },
  ];

  const [openFilters, setOpenFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState(initialFilters);


  // ✅ Helper to fetch filtered candidates or all
  const fetchCandidates = useCallback(async (filtersObj = {}) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let response;

      if (Object.keys(filtersObj).length === 0) {
        // no filters → get all
        response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/filterCandidates`, { headers });
      } else {
        // Handle array params correctly
        const params = new URLSearchParams();
        Object.entries(filtersObj).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(val => params.append(key, val));
          } else {
            params.append(key, value);
          }
        });

        response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/institutionfilter/filterCandidates?${params.toString()}`,
          { headers }
        );
      }

      onResults(response.data || []);
    } catch (error) {
      console.error("Error fetching filtered candidates:", error);
      // onResults([]); // Optional: keep previous results or clear on error
      if (error.response && error.response.status === 401) {
        // Handle unauthorized, maybe redirect to login or show alert
        console.error("Unauthorized access to filters.");
      }
    }
  }, [onResults]);

  // Sync state if initialFilters prop changes
  React.useEffect(() => {
    if (!initialFilters || Object.keys(initialFilters).length === 0) return;

    // Clean filters: remove keys with empty arrays or null/undefined
    const cleanedFilters = {};
    Object.entries(initialFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) cleanedFilters[key] = value;
      } else if (value) {
        cleanedFilters[key] = value;
      }
    });

    if (Object.keys(cleanedFilters).length > 0) {
      setSelectedFilters(cleanedFilters);
      // Immediately fetch candidates with these filters
      fetchCandidates(cleanedFilters);
    }
  }, [initialFilters, fetchCandidates]);

  // Toggle open/close each filter section
  const toggleFilter = (label) => {
    setOpenFilters((prev) => ({ ...prev, [label]: !prev[label] }));
  };



  // When user clicks an option
  const handleOptionSelect = (filterKey, option) => {
    // Multi-select logic for 'skill' (Subject)
    if (filterKey === 'skill') {
      const currentVal = selectedFilters[filterKey];
      const currentArray = Array.isArray(currentVal) ? currentVal : (currentVal ? [currentVal] : []);

      let newArray;
      if (currentArray.includes(option)) {
        newArray = currentArray.filter(item => item !== option);
      } else {
        newArray = [...currentArray, option];
      }

      const newFilters = { ...selectedFilters };
      if (newArray.length > 0) {
        newFilters[filterKey] = newArray;
      } else {
        delete newFilters[filterKey];
      }

      setSelectedFilters(newFilters);
      fetchCandidates(newFilters);
    } else {
      // Single-select for others
      // Strip ' km' from distance values before storing
      const cleanedValue = filterKey === 'distance' ? option.replace(/\s*km/i, '') : option;
      const newFilters = { ...selectedFilters, [filterKey]: cleanedValue };
      setSelectedFilters(newFilters);
      fetchCandidates(newFilters);
    }
  };

  // ✅ Remove individual filter
  const handleRemoveFilter = (filterKey, valueToRemove = null) => {
    const updatedFilters = { ...selectedFilters };

    if (valueToRemove && Array.isArray(updatedFilters[filterKey])) {
      // Remove specific value from array
      const newArray = updatedFilters[filterKey].filter(val => val !== valueToRemove);
      if (newArray.length > 0) {
        updatedFilters[filterKey] = newArray;
      } else {
        delete updatedFilters[filterKey];
      }
    } else {
      // Remove entire key
      delete updatedFilters[filterKey];
    }

    setSelectedFilters(updatedFilters); // Update state

    if (Object.keys(updatedFilters).length === 0) {
      // No filters remain → fetch all candidates
      fetchCandidates({});
    } else {
      fetchCandidates(updatedFilters);
    }
  };

  // ✅ Clear all filters
  const handleClearFilters = () => {
    setSelectedFilters({});
    fetchCandidates({});
  };

  return (
    <div className='global-font'>
      <div className="w-full p-4 space-y-4 bg-white rounded-lg shadow-md sm:w-64">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center space-x-2 text-lg font-semibold text-gray-700">
            <Filter className="text-blue-500" />
            <span>Filter Teachers</span>
          </h2>

          {Object.keys(selectedFilters).length > 0 && (
            <button
              onClick={handleClearFilters}
              className="flex items-center px-2 py-1 text-sm text-red-500 rounded hover:bg-red-50"
            >
              <XCircle size={16} className="mr-1" /> Clear All
            </button>
          )}
        </div>

        {/* ✅ Active Filter Tags */}
        {Object.keys(selectedFilters).length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 rounded-md bg-gray-50">
            {Object.entries(selectedFilters).flatMap(([key, value]) => {
              if (Array.isArray(value)) {
                return value.map(val => (
                  <div
                    key={`${key}-${val}`}
                    className="flex items-center px-2 py-1 text-sm text-blue-600 bg-blue-100 rounded-full"
                  >
                    <span>{val}</span>
                    <button
                      onClick={() => handleRemoveFilter(key, val)}
                      className="ml-1 text-blue-500 hover:text-blue-700"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ));
              }
              // Skip rendering if value is empty/null/undefined
              if (!value) return null;

              return (
                <div
                  key={key}
                  className="flex items-center px-2 py-1 text-sm text-blue-600 bg-blue-100 rounded-full"
                >
                  <span>{value}</span>
                  <button
                    onClick={() => handleRemoveFilter(key)}
                    className="ml-1 text-blue-500 hover:text-blue-700"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter list */}
        {filters.map((filter) => (
          <div key={filter.label}>
            <div
              className="flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-gray-100"
              onClick={() => toggleFilter(filter.label)}
            >
              <span className="text-sm font-medium text-gray-600">{filter.label}</span>
              <ChevronDown
                className={`text-blue-500 transform transition-transform ${openFilters[filter.label] ? "rotate-180" : ""
                  }`}
              />
            </div>

            {openFilters[filter.label] && (
              <div className="pl-4 mt-2 space-y-2">
                {filter.options.map((option, idx) => {
                  const isSelected = Array.isArray(selectedFilters[filter.key])
                    ? selectedFilters[filter.key].includes(option)
                    : selectedFilters[filter.key] === option;

                  return (
                    <div
                      key={idx}
                      onClick={() => handleOptionSelect(filter.key, option)}
                      className={`p-2 text-sm text-gray-700 rounded-md cursor-pointer hover:bg-gray-100 ${isSelected
                        ? "bg-blue-100 text-blue-600 font-semibold"
                        : ""
                        }`}
                    >
                      {option}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FilterSidebar;